import React, { useState, useEffect, useCallback } from 'react';
import './MessagesImageLightbox.scss';
import { CloseIcon } from '../common/CloseIcon';

interface ImageAttachment {
  id: string;
  fileName: string;
  storage: {
    url: string;
    blobPath?: string;
    thumbnailUrl?: string;
  };
  imageMeta?: {
    width: number;
    height: number;
  };
}

interface ImageLightboxProps {
  images: ImageAttachment[];
  initialIndex: number;
  onClose: () => void;
}

const MessagesImageLightbox: React.FC<ImageLightboxProps> = ({ images, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(() => {
    const idx = Number.isFinite(initialIndex) ? initialIndex : 0;
    return Math.max(0, Math.min(idx, Math.max(0, images.length - 1)));
  });
  const [isZoomed, setIsZoomed] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Ensure valid index and close when there are no images
  useEffect(() => {
    if (images.length === 0) {
      onClose();
      return;
    }
    setCurrentIndex((prev) => {
      const maxIndex = Math.max(0, images.length - 1);
      return Math.max(0, Math.min(prev, maxIndex));
    });
  }, [images.length, onClose]);

  const currentImage = images[currentIndex];

  const handlePrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    setIsZoomed(false);
  }, [images.length]);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    setIsZoomed(false);
  }, [images.length]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowLeft') {
      handlePrevious();
    } else if (e.key === 'ArrowRight') {
      handleNext();
    }
  }, [onClose, handlePrevious, handleNext]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      handleNext();
    } else if (isRightSwipe) {
      handlePrevious();
    }
  };

  const getBlobPath = (): string => {
    if (!currentImage) return '';
    if (currentImage.storage.blobPath) return currentImage.storage.blobPath;
    try {
      const u = new URL(currentImage.storage.url);
      const path = u.pathname.replace(/^\//, '');
      const parts = path.split('/');
      if (parts.length > 1) return parts.slice(1).join('/');
      return path;
    } catch {
      return '';
    }
  };

  const handleDownload = async () => {
    try {
      if (!currentImage) return;
      const blobPath = getBlobPath();
      const res = await fetch(`${process.env.REACT_APP_API_URL}/attachments/download-url`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          blobPath: blobPath || '',
          fileName: currentImage.fileName,
          mimeType: 'image/jpeg'
        })
      });
      if (!res.ok) throw new Error('Failed to get download URL');
      const { url } = await res.json();
      const link = document.createElement('a');
      link.href = url;
      link.download = currentImage.fileName;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error('Download failed', e);
      if (currentImage) window.open(currentImage.storage.url, '_blank', 'noopener');
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="image-lightbox-messages-modal" onClick={handleBackdropClick}>
      <div className="image-lightbox-messages-modal__header">
        <div className="image-lightbox-messages-modal__counter">
          {currentIndex + 1} / {images.length}
        </div>
        
        <div className="image-lightbox-messages-modal__actions">
          <button
            className="image-lightbox-messages-modal__action"
            onClick={handleDownload}
            aria-label="Download image"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </button>
          
          <button
            className="image-lightbox-messages-modal__action"
            onClick={() => setIsZoomed(!isZoomed)}
            aria-label="Toggle zoom"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
              {isZoomed ? (
                <path d="M8 11h6" />
              ) : (
                <path d="M11 8v6M8 11h6" />
              )}
            </svg>
          </button>
          
          <button
            className="image-lightbox-messages-modal__close"
            onClick={onClose}
            aria-label="Close lightbox"
          >
            <CloseIcon size={24} color="white"/>
          </button>
        </div>
      </div>

      <div 
        className="image-lightbox-messages-modal__content"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {images.length > 1 && (
          <button
            className="image-lightbox-messages-modal__nav image-lightbox-messages-modal__nav--prev"
            onClick={handlePrevious}
            aria-label="Previous image"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        )}

        {currentImage && (
          <img
            src={currentImage.storage.url}
            alt={currentImage.fileName}
            className={`image-lightbox-messages-modal__image ${isZoomed ? 'image-lightbox-messages-modal__image--zoomed' : ''}`}
            onClick={() => setIsZoomed(!isZoomed)}
          />
        )}

        {images.length > 1 && (
          <button
            className="image-lightbox-messages-modal__nav image-lightbox-messages-modal__nav--next"
            onClick={handleNext}
            aria-label="Next image"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        )}
      </div>

      {images.length > 1 && (
        <div className="image-lightbox-messages-modal__thumbnails">
          {images.map((image, index) => (
            <button
              key={image.id}
              className={`image-lightbox-messages-modal__thumbnail ${index === currentIndex ? 'image-lightbox-messages-modal__thumbnail--active' : ''}`}
              onClick={() => setCurrentIndex(index)}
              aria-label={`Go to image ${index + 1}`}
            >
              <img
                src={image.storage.thumbnailUrl || image.storage.url}
                alt={image.fileName}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MessagesImageLightbox;


